'use client'

import { LoadingState } from '@repo/ui/LoadingState'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useClientStore } from '@repo/store/useClientStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useState, useEffect } from 'react'
import { ExtensionField } from '@repo/ui/ExtensionField'
import MUIAlert from '@mui/material/Alert'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from '../MatterDrawerMatterTab/MatterDrawerMatterTab.module.scss'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'

export const MatterDrawerClientExtensionsTab = ({ workspaceDetailCardVisible = false }) => {
  const { matters, matterDrawerIsOpen, matterDrawerClientMatterNo } = useMattersStore()
  const { clients, currentClient, currentClientNo } = useClientStore()
  const { fetchClientExtensions } = useCustomDataPageStore()
  const { clientNo } = currentClient || {}
  const [client, setClient] = useState(null)
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [clientExtensions, setClientExtensions] = useState(null)

  useEffect(() => {
    if (matterDrawerIsOpen && matterDrawerClientMatterNo) {
      const { clientNo } = extractClientMatterNumbers({ clientMatterNo: matterDrawerClientMatterNo }) || {}
      const client = clients[clientNo]
      setClient(client)
    }

    if (!matterDrawerClientMatterNo && !matterDrawerIsOpen && workspaceDetailCardVisible && currentClientNo) {
      setClient(currentClient)
    }
  }, [
    matterDrawerClientMatterNo,
    matterDrawerIsOpen,
    workspaceDetailCardVisible,
    matters,
    currentClient,
    clients,
    currentClientNo
  ])

  useEffect(() => {
    const getClientExtensions = async () => {
      setIsFetchingData(true)
      setClientExtensions(null)
      const { namedClients, clientNo } = client || {}
      let clientType = null
      if (namedClients?.length) {
        clientType = namedClients[0].clientType
      }

      if (!clientType) {
        setIsFetchingData(false)
        return
      }

      console.log('Fetching client extensions for clientNo:', clientNo, 'clientType:', clientType)
      const extensions = await fetchClientExtensions({ clientNo, clientType, includeValues: true })
      console.log(`Client extensions for ${clientNo}:`, extensions)
      setClientExtensions(extensions || null)
      setIsFetchingData(false)
    }

    if (client) getClientExtensions()
  }, [
    workspaceDetailCardVisible,
    clientNo,
    matterDrawerClientMatterNo,
    matterDrawerIsOpen,
    client,
    fetchClientExtensions,
    currentClientNo
  ])

  if (
    isFetchingData ||
    (!workspaceDetailCardVisible && !matterDrawerClientMatterNo) ||
    (workspaceDetailCardVisible && !currentClientNo) ||
    !client
  ) {
    return (
      <MUIStack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerClientExtensionsTab"
      >
        <LoadingState />
      </MUIStack>
    )
  }

  if (!clientExtensions && !isFetchingData) {
    return (
      <MUIStack
        justifyContent="flex-start"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerClientExtensionsTab"
      >
        <MUIAlert severity="info" sx={{ m: '30px 16px 16px' }}>
          No client extensions found for this matter
        </MUIAlert>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="MatterDrawerClientExtensionsTab"
      role="tabpanel"
      className={styles.MatterDrawerMatterTab}
      sx={{ padding: '24px', width: '100%' }}
    >
      <MUIStack
        className={styles.MatterDrawerMatterTab}
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%', position: 'relative' }}
        flexWrap="wrap"
        gap={8}
      >
        {clientExtensions?.map((extension) => (
          <MUIStack gap={4} key={extension.questionnaireId} sx={{ width: '100%' }}>
            <MUITypography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                mb: 0,
                ml: 3,
                mt: 3
              }}
            >
              {extension?.description}
            </MUITypography>

            {extension?.fields?.map((field, index) => {
              return (
                <ExtensionField
                  width={'100%'}
                  field={field}
                  index={index}
                  extension={extension}
                  key={`${field.fieldId}-${field.pageId}-${index}`}
                />
              )
            })}
          </MUIStack>
        ))}
      </MUIStack>
    </MUIStack>
  )
}

MatterDrawerClientExtensionsTab.propTypes = {
  workspaceDetailCardVisible: PropTypes.bool
}
